import JumboCardQuick from '@jumbo/components/JumboCardQuick';
import { List, PictureAsPdf } from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Delete';
import DescriptionIcon from '@mui/icons-material/Description';
import {
    Button,
    ButtonGroup,
    Card,
    Checkbox,
    Chip,
    FormControl,
    FormControlLabel,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { getYKKReportDataApi, generateYKKReportPDFApi, generateYKKReportWORDApi } from 'api/report';
import LoadingPopUp from 'app/components/LoadingPopup';
import { getDateStringYYYYMMDD, isRoleCheckList } from 'app/utils/appHelpers';
import config, { prodYKToplantisiUnitId } from 'config';
import 'dayjs/locale/tr';
import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';

// specificOtherDepts dizisi, istenen yapıda her zaman son kısımda alfabetik sıralı olarak eklenecek.
const specificOtherDepts = ["SGDB", "IKDHB", "BÇKDB", "HM", "İç Denetim Birimi"].sort();

/*
  Type "yk" için zorunlu (non-removable) departman değerlerini belirleyen yardımcı fonksiyon:
  Eğer userRoles içinde:
    - 416 id'li rol varsa VE rolün inst değeri "YK" ise
    - veya 370/372 id'li roller varsa ve bunların inst değeri "YK" ise,
  bu durumda yapı olduğu gibi kalacak (departman seçeneklerinde tüm departmanlar kullanılacak).
  Aksi durumda, 
    - eğer 370/372 id'li roller yoksa ve 416 varsa fakat inst "YK" değil
    - veya 373/369 id'li roller varsa,
  ilgili rollerden unique inst değerleri alınarak, departman seçeneklerine eklenir ve
  sonrasında alfabetik sıralı specificOtherDepts dizisi eklenir.
*/
const getRequiredDeptsForYK = (userRoles) => {
    const has416_YK = userRoles.some(r => r.id === 416 && r.inst.toUpperCase() === "YK");
    const has370or372_YK = userRoles.some(r => [370, 372].includes(r.id) && r.inst.toUpperCase() === "YK");

    // Eğer 416 veya 370/372 id'li roller varsa ve inst değeri "YK" ise yapı olduğu gibi çalışsın.
    if (has416_YK || has370or372_YK) {
        return null;
    }

    // Eğer 370/372 rolleri bulunmuyorsa fakat 416 varsa fakat inst "YK" değil
    // veya 373 veya 369 id'li roller varsa:
    const condition = (
        (!userRoles.some(r => [370, 372].includes(r.id)) && userRoles.some(r => r.id === 416 && r.inst.toUpperCase() !== "YK"))
        || userRoles.some(r => [373, 369].includes(r.id))
    );

    if (condition) {
        const insts = userRoles
            .filter(r => ((r.id === 416 && r.inst.toUpperCase() !== "YK") || [373, 369].includes(r.id)))
            .map(r => r.inst);
        const uniqueInsts = Array.from(new Set(insts));

        return [...uniqueInsts, ...specificOtherDepts];
    }
    return null;
};

function ReportYKKPage() {
    const defaultStartDate = new Date();
    defaultStartDate.setDate(1);
    defaultStartDate.setMonth(0);
    const defaultFinishedDate = new Date();

    const authUser = useSelector(state => state.auth.authUser);

    const [state, setState] = useState({
        inst: 'YK', // YK sabit
        startedDate: null,
        finishedDate: null,
        department: [], // Array olarak değiştirildi
        meetingNo: '',
        applied: false,
        notApplied: false,
        showStatus: false // Durumlarını Göster checkbox'ı
    });

    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [lastFilter, setLastFilter] = useState(null);
    const [dataDepartmentOptions, setDataDepartmentOptions] = useState([]);
    const [fixedDepartment, setFixedDepartment] = useState(null);

    // Zorunlu alanlar kontrol fonksiyonu
    const isFormValid = () => {
        return state.startedDate && state.finishedDate;
    };

    // Departments yetki kontrolü
    useEffect(() => {
        const requiredDeps = getRequiredDeptsForYK(authUser?.userRoles || []);
        if (requiredDeps) {
            let updatedDepartments;
            const nonSpecific = requiredDeps.filter(dep => !specificOtherDepts.includes(dep));
            if (nonSpecific.length === 1) {
                updatedDepartments = config.departments
                    .map(item => item.name)
                    .filter(d => d === nonSpecific[0] || specificOtherDepts.includes(d));
            } else {
                updatedDepartments = config.departments.map(item => item.name);
            }
            setDataDepartmentOptions(updatedDepartments);
            setFixedDepartment(nonSpecific);

            // Zorunlu departmanları state'e ekle
            if (nonSpecific.length > 0) {
                setState(prev => ({
                    ...prev,
                    department: [...nonSpecific] // Tüm zorunlu departmanları array olarak ekle
                }));
            }
        } else {
            // Eğer 416 (veya 370/372) id'li rol varsa ve inst değeri "YK" ise; tüm departmanlar kullanılsın.
            setDataDepartmentOptions(config.departments.map(item => item.name));
            setFixedDepartment(null);
        }
    }, [authUser]);

    // Filtrelerden status değerini hesapla
    const getStatusFilter = () => {
        if (state.applied && !state.notApplied) {
            return 1; // Uygulananlar
        } else if (!state.applied && state.notApplied) {
            return 0; // Uygulanmayanlar
        }
        return null; // Tümü
    };

    // Listele
    const handleResultList = async () => {
        const newStartDate = getDateStringYYYYMMDD(state.startedDate, "00:00:01");
        const newFinishedDate = getDateStringYYYYMMDD(state.finishedDate, "23:59:59");

        const filters = {
            inst: 'YK', // YK sabit
            unitId: prodYKToplantisiUnitId, // prodYKToplantisiUnitId
            startedDate: newStartDate,
            finishedDate: newFinishedDate,
            department: state.department && state.department.length > 0 ? state.department : null, // Array veya null
            meetingNo: state.meetingNo || null,
            status: getStatusFilter()
        };

        setLoading(true);
        try {
            const res = await getYKKReportDataApi(filters);
            setData(res.data.data);

            // Department longName'i bul (ilk seçili departmanın longName'i kullanılacak) ve büyük harfe çevir
            let departmentLabel = state.inst;
            if (state.department && state.department.length > 0) {
                const dept = config.departments.find(d => d.name === state.department[0]);
                departmentLabel = dept ? dept.longName : state.department[0];
            }
            // Türkçe karakterlere dikkat ederek büyük harfe çevir
            departmentLabel = departmentLabel.toLocaleUpperCase('tr-TR');

            setLastFilter({
                ...filters,
                departmentLabel: departmentLabel,
                showStatus: state.showStatus // Durumlarını Göster checkbox durumu
            });
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    // PDF oluştur
    const handleGeneratePDF = async () => {
        if (!lastFilter) return;
        setLoading(true);
        try {
            await generateYKKReportPDFApi(lastFilter);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    // Word oluştur
    const handleGenerateWORD = async () => {
        if (!lastFilter) return;
        setLoading(true);
        try {
            await generateYKKReportWORDApi(lastFilter, "YKK_Rapor");
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    // Temizle
    const handleClear = () => {
        // Temizleme sırasında zorunlu departmanları koru
        let defaultDepartments = [];
        if (fixedDepartment && Array.isArray(fixedDepartment) && fixedDepartment.length > 0) {
            defaultDepartments = [...fixedDepartment];
        }

        setState({
            inst: 'YK', // YK sabit
            startedDate: null,
            finishedDate: null,
            department: defaultDepartments,
            meetingNo: '',
            applied: false,
            notApplied: false,
            showStatus: false
        });
        setLastFilter(null);
        setData([]);
    };

    // İlgili Kurum/Birim değişikliği kontrolü (decisionStatusPage'deki gibi)
    const handleDepartmentChange = (event, newValue) => {
        if (fixedDepartment && Array.isArray(fixedDepartment)) {
            // Eğer zorunlu departmanlar varsa, kullanıcı en az sabit (fixedDepartment) değerlerden bir tanesini silmemeli.
            const fixedSelected = fixedDepartment.filter(dep => newValue.includes(dep));
            // Eğer hiç fixed değerden seçili kalmadıysa, son sabiti tekrar ekle.
            if (fixedSelected.length === 0) {
                newValue = [...newValue, fixedDepartment[fixedDepartment.length - 1]];
            }
            setState(prevState => ({ ...prevState, department: newValue }));
        } else {
            setState(prevState => ({ ...prevState, department: newValue }));
        }
    };

    return (
        <div>
            <LoadingPopUp open={loading} />
            <JumboCardQuick title="YKK Rapor">
                <Grid container spacing={2} mb={2}>
                    {/* Satır 1: Kurum (sabit YK), İlgili Konu/Birim, Toplantı Sayısı, Uygulananlar, Uygulanmayanlar */}
                    <Grid item xs={12} sm={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel id="inst-label">Kurum *</InputLabel>
                            <Select
                                labelId="inst-label"
                                label="Kurum *"
                                value="YK"
                                disabled
                                required
                            >
                                <MenuItem value="YK">YK</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={3}>
                        <Autocomplete
                            multiple
                            size="small"
                            id="department-autocomplete"
                            options={dataDepartmentOptions}
                            value={state.department}
                            onChange={handleDepartmentChange}
                            renderInput={(params) => (
                                <TextField {...params} label="İlgili Konu/Birim" placeholder="Seçiniz" variant="outlined" size="small" />
                            )}
                        />
                    </Grid>

                    <Grid item xs={12} sm={3}>
                        <TextField
                            fullWidth
                            label="Toplantı Sayısı"
                            variant="outlined"
                            size="small"
                            value={state.meetingNo}
                            onChange={(e) =>
                                setState(prevState => ({ ...prevState, meetingNo: e.target.value }))
                            }
                        />
                    </Grid>

                    <Grid item xs={12} sm={1.5}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={state.applied}
                                    disabled={state.notApplied}
                                    onChange={(e) => setState(prev => ({ ...prev, applied: e.target.checked }))}
                                />
                            }
                            label="Uygulananlar"
                        />
                    </Grid>

                    <Grid item xs={12} sm={1.5}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={state.notApplied}
                                    disabled={state.applied}
                                    onChange={(e) => setState(prev => ({ ...prev, notApplied: e.target.checked }))}
                                />
                            }
                            label="Uygulanmayanlar"
                        />
                    </Grid>

                    {/* Satır 2: Tarihler ve Durumlarını Göster checkbox'ı */}
                    <Grid item xs={12} sm={3}>
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="tr">
                            <DatePicker
                                label="Başlangıç Tarihi *"
                                size="small"
                                value={state.startedDate}
                                onChange={(newValue) =>
                                    setState(prevState => ({ ...prevState, startedDate: newValue }))
                                }
                                renderInput={(params) => <TextField {...params} size="small" fullWidth />}
                            />
                        </LocalizationProvider>
                    </Grid>

                    <Grid item xs={12} sm={3}>
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="tr">
                            <DatePicker
                                label="Bitiş Tarihi *"
                                size="small"
                                value={state.finishedDate}
                                onChange={(newValue) =>
                                    setState(prevState => ({ ...prevState, finishedDate: newValue }))
                                }
                                renderInput={(params) => <TextField {...params} size="small" fullWidth />}
                            />
                        </LocalizationProvider>
                    </Grid>

                    <Grid item xs={12} sm={3}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={state.showStatus}
                                    onChange={(e) => setState(prev => ({ ...prev, showStatus: e.target.checked }))}
                                />
                            }
                            label="Durumlarını Göster"
                        />
                    </Grid>

                    {/* Satır 3: Butonlar */}
                    <Grid item xs={12}>
                        <ButtonGroup>
                            <Button
                                variant="outlined"
                                size="medium"
                                color="success"
                                startIcon={<List />}
                                onClick={handleResultList}
                                disabled={!isFormValid()}
                            >
                                Listele
                            </Button>
                            <Button
                                variant="outlined"
                                size="medium"
                                color="success"
                                startIcon={<PictureAsPdf />}
                                disabled={!lastFilter || data.length === 0}
                                onClick={handleGeneratePDF}
                            >
                                PDF
                            </Button>
                            <Button
                                variant="outlined"
                                size="medium"
                                color="primary"
                                startIcon={<DescriptionIcon />}
                                disabled={!lastFilter || data.length === 0}
                                onClick={handleGenerateWORD}
                            >
                                WORD
                            </Button>
                            <Button
                                variant="outlined"
                                size="medium"
                                color="error"
                                startIcon={<DeleteIcon />}
                                onClick={handleClear}
                            >
                                TEMİZLE
                            </Button>
                        </ButtonGroup>
                    </Grid>
                </Grid>
            </JumboCardQuick>

            {/* Sonuç Tabloları - meetingNo'ya göre gruplanmış */}
            {data.length > 0 ? (() => {
                // meetingNo'ya göre grupla
                const groupedByMeetingNo = {};
                data.forEach(item => {
                    const meetingNo = item.meetingNo || '';
                    if (!groupedByMeetingNo[meetingNo]) {
                        groupedByMeetingNo[meetingNo] = [];
                    }
                    groupedByMeetingNo[meetingNo].push(item);
                });

                // meetingNo'ları sırala
                const meetingNos = Object.keys(groupedByMeetingNo).sort((a, b) => {
                    const numA = parseInt(a) || 0;
                    const numB = parseInt(b) || 0;
                    return numA - numB;
                });

                return meetingNos.map((meetingNo, meetingIndex) => {
                    const items = groupedByMeetingNo[meetingNo];
                    const totalCount = items.length;
                    return (
                        <Card key={meetingNo} sx={{ padding: 2, marginTop: meetingIndex > 0 ? 3 : 2 }}>
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', marginBottom: 16 }}>
                                <Typography variant="h6" sx={{ textAlign: 'center' }}>
                                    {lastFilter?.departmentLabel || lastFilter?.inst} - {meetingNo || '...'} SAYILI YÖNETİM KURULU KARARLARI UYGULANMA DURUMLARI
                                </Typography>
                                <Chip
                                    label={`Toplam: ${totalCount}`}
                                    color="primary"
                                    size="small"
                                    sx={{ fontWeight: 'bold' }}
                                />
                            </div>
                            <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                                <colgroup>
                                    <col style={{ width: '12%' }} />
                                    <col style={{ width: '8%' }} />
                                    <col style={{ width: '15%' }} />
                                    <col style={{ width: '45%' }} />
                                    <col style={{ width: '20%' }} />
                                </colgroup>
                                <thead>
                                    <tr style={{ backgroundColor: '#f5f5f5' }}>
                                        <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>Tarihi</th>
                                        <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>Sayısı</th>
                                        <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>Konusu</th>
                                        <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>Yönetim Kurulu Kararı</th>
                                        <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>Uygulanma Durumu</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((item) => (
                                        <tr key={item.id}>
                                            <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>
                                                {item.meetingDate ? new Date(item.meetingDate).toLocaleDateString('tr-TR') : ''}
                                            </td>
                                            <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>
                                                {item.decisionNo || ''}
                                            </td>
                                            <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                                                {item.subjectName}
                                            </td>
                                            <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                                                {item.decision ? item.decision.replace(/<[^>]*>/g, '') : ''}
                                            </td>
                                            <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>
                                                {state.showStatus ? (
                                                    item.status === 1 ? (
                                                        'Uygulandı'
                                                    ) : item.status === 0 ? (
                                                        <span style={{ color: '#d32f2f', fontWeight: 'bold' }}>Uygulanmadı</span>
                                                    ) : ''
                                                ) : ''}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </Card>
                    );
                });
            })() : (
                !loading && lastFilter && (
                    <Card sx={{ padding: 2, marginTop: 2 }}>
                        <Typography align="center" variant="h6">
                            Sonuç Bulunamadı
                        </Typography>
                    </Card>
                )
            )}
        </div>
    );
}

export default ReportYKKPage;

